// import external libraries
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// import constants
import { BACKLOG_PREFERENCES_STORAGE_KEY } from './Backlog.consts';

// import types
import type {
  BacklogEmployeeViewFilters,
  BacklogPreferencesState,
  BacklogPreferencesStore
} from './Backlog.types';

/**
 * What the takenlijst opens on until the planner changes something: no narrowing filters, the signed
 * difference in the cells, and both the totals column and the fulfilled weeks shown. The same defaults
 * the page has always had — now the starting point a first visit falls back to, and what a reset
 * restores to.
 */
export const BACKLOG_PREFERENCES_DEFAULTS: BacklogPreferencesState = {
  rowEntityType: 'CLIENT',
  entityFilters: [],
  employeeViewFilters: { planUnitIds: [], employees: [], functions: [] },
  activityIds: [],
  cellValueMode: 'difference',
  showTotals: true,
  showCompleted: true
};

/**
 * v1 persisted `activityCodes`/`careReceiverIds`, which version 2 replaces with
 * `entityFilters`/`activityIds` — drop the stale keys rather than carry them forward unused, and seed
 * the new ones empty rather than guess a mapping from the old shape. v3 adds the row entity type, which
 * neither earlier version had: the page only ever showed care receivers, so that is what a remembered
 * selection means. v4 adds the employee view's own filters, which no earlier version remembered at all, so
 * they start empty. Exported so the migration itself is unit-testable without going through zustand's
 * rehydration timing.
 */
export const migrateBacklogPreferences = (
  persisted: unknown,
  fromVersion: number
) => {
  const stored = (persisted ?? {}) as Record<string, unknown>;
  const withRowEntityType =
    fromVersion < 3 ? { ...stored, rowEntityType: 'CLIENT' } : stored;
  const withEmployeeViewFilters =
    fromVersion < 4
      ? {
          ...withRowEntityType,
          employeeViewFilters: {
            planUnitIds: [],
            employees: [],
            functions: []
          }
        }
      : withRowEntityType;

  if (fromVersion < 2) {
    const {
      activityCodes: _activityCodes,
      careReceiverIds: _careReceiverIds,
      ...rest
    } = withEmployeeViewFilters;
    return { ...rest, entityFilters: [], activityIds: [] };
  }
  return withEmployeeViewFilters;
};

/**
 * The persisted state folded onto the defaults, with every collection filled in.
 *
 * Zustand's own merge is shallow, so a stored object <em>replaces</em> its default rather than filling the
 * gaps in it: a stored {@code employeeViewFilters} written before a field existed — or written with one
 * undefined, which {@code toFormValues} will happily hand back — arrives with that key missing. The page
 * then reads it as an array and the whole takenlijst fails to render: {@code planUnitIds.includes} on
 * undefined, before a single request goes out, so the planner gets a blank page and nothing to go on.
 *
 * {@link migrateBacklogPreferences} cannot catch this, because it only runs when the stored version differs
 * — and a shape can rot within one version. This runs on every rehydration, so stored state that is already
 * broken is repaired on the next load rather than needing the planner to clear their browser storage.
 */
export const mergeBacklogPreferences = (
  persisted: unknown,
  current: BacklogPreferencesStore
): BacklogPreferencesStore => {
  const stored = (persisted ?? {}) as Partial<BacklogPreferencesState>;
  // Partial, because that is the whole point: a stored shape can be missing any of the three.
  const storedEmployeeViewFilters: Partial<BacklogEmployeeViewFilters> =
    stored.employeeViewFilters ?? {};

  return {
    ...current,
    ...stored,
    entityFilters:
      stored.entityFilters ?? BACKLOG_PREFERENCES_DEFAULTS.entityFilters,
    activityIds: stored.activityIds ?? BACKLOG_PREFERENCES_DEFAULTS.activityIds,
    employeeViewFilters: {
      planUnitIds:
        storedEmployeeViewFilters.planUnitIds ??
        BACKLOG_PREFERENCES_DEFAULTS.employeeViewFilters.planUnitIds,
      employees:
        storedEmployeeViewFilters.employees ??
        BACKLOG_PREFERENCES_DEFAULTS.employeeViewFilters.employees,
      functions:
        storedEmployeeViewFilters.functions ??
        BACKLOG_PREFERENCES_DEFAULTS.employeeViewFilters.functions
    }
  };
};

/**
 * Remembers which planning entity is on screen and the filter and view-settings popovers across visits, so
 * a planner does not set them again every time the page opens. Persisted to localStorage.
 *
 * A remembered activity or care receiver code is safe to keep even after it leaves the data: the page
 * matches the stored ids against whatever the current response offers, so a code that no longer appears
 * simply stops narrowing rather than hiding everything.
 */
export const useBacklogPreferencesStore = create<BacklogPreferencesStore>()(
  persist(
    (set) => ({
      ...BACKLOG_PREFERENCES_DEFAULTS,
      setRowEntityType: (rowEntityType) => set({ rowEntityType }),
      setEntityFilters: (entityFilters) => set({ entityFilters }),
      setEmployeeViewFilters: (employeeViewFilters) =>
        set({ employeeViewFilters }),
      setActivityIds: (activityIds) => set({ activityIds }),
      setCellValueMode: (cellValueMode) => set({ cellValueMode }),
      setShowTotals: (showTotals) => set({ showTotals }),
      setShowCompleted: (showCompleted) => set({ showCompleted })
    }),
    {
      name: BACKLOG_PREFERENCES_STORAGE_KEY,
      version: 4,
      // Only the settings, never the setters — persisting the whole state would write functions that
      // JSON drops anyway.
      partialize: ({
        rowEntityType,
        entityFilters,
        employeeViewFilters,
        activityIds,
        cellValueMode,
        showTotals,
        showCompleted
      }) => ({
        rowEntityType,
        entityFilters,
        employeeViewFilters,
        activityIds,
        cellValueMode,
        showTotals,
        showCompleted
      }),
      migrate: migrateBacklogPreferences,
      merge: mergeBacklogPreferences
    }
  )
);
